import React, {
    useState, useRef, useEffect,
} from 'react';
import { message, Steps } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    useStores,
    useEmailOTPFlow,
    useLoginMethodPreference,
    useAuthResolved,
    LOGIN_METHOD_EMAIL,
    LOGIN_METHOD_OTP,
} from '@/components/hooks';
import {
    useVerifyAction,
} from '@/components/MiniApps/RecipientPage/InnerContent/LinkView/hooks';
import styles from './OTPAuthSteps.module.scss';
import OTPAuthEmailBox from './OTPAuthEmailBox';
import OTPAuthCodeBox from './OTPAuthCodeBox';
import OTPAuthPasswordBox from './OTPAuthPasswordBox';
import OTPAuthPhoneBox from './OTPAuthPhoneBox';
import { PhoneMissingModal, PasswordMissingModal } from './OTPAuthStepsMessages';
import {
    ALLOW_FORWARDING,
    REQUIRED_PASSWORD_VERIFICATION,
    CURRENT_ORGANIZATION,
    OTP_ADMINS_RESTRICTED,
    OTP_COLLABORATORS_RESTRICTED,
    RECIPIENT_EMAIL_FOR_LOGIN,
} from '@/consts';
import { BASEURL, ENDPOINTS, getFile } from '@/api';
import { isOfficeFile, isMaskedEmail, firstCleartextEmail } from '@/components/utils';
import { type CognitoAuthError, type PhoneIdentity, SimpleCallback } from '@/types/types';
import getPhoneDestination from './phoneDestination';
import EmailChangeMessage from './OTPAuthStepsMessages/EmailChangeMessage';
import { trySignUp, InvalidLinkError, getLinkIdFromLocation } from '@/components/hooks/useOTPFlow/helpers';
import {
    useFilesActionsContext,
} from '@/components/MiniApps/RecipientPage/InnerContent/LinkView/hooks/useFilesActionContext.ts';
import './index.scss';
import {
    caseInsensitiveUrlParam,
    parseAuthErrorType,
    saveRedirectPath,
    checkMessageContainsPermissions,
    getErrorResponse,
    checkIsNetworkError,
    checkIsMFARequired as checkIsMFARequiredAPI,
    checkIsMFAPasswordRequired as checkIsMFAPasswordRequiredAPI,
} from '@/components/utils';
import { CognitoErrorCodes } from '@/types/types';
import i18n from '@/content';
import { OpenOptions } from '@/config/openOptions.ts';
import Spinner from '@/components/Common/Spin';
import appConfig from '@/config/env';
import {
    clearPhoneMfaCompletedForLink,
    isPhoneMfaCompletedForLink,
    markPhoneMfaCompletedForLink,
} from '../constants';
import { hasPlatformSession, resolvePhoneGate, type MfaProbeResult } from './authFlowRouting';

const { Step } = Steps;
const nameSpace = 'recipientPage';
type CodeSendingMethod = 'phone' | 'voice';

// Guard against duplicate auto-sends of the OTP code triggered by the initial
// `useEffect` running more than once (dep toggle or component remount).
// Lives in module scope so it survives the component's unmount/remount cycle,
// sends (Submit / Resend) bypass this guard entirely.
const autoSendGuard = new Set<string>();
const shouldSkipAutoSend = (key: string): boolean => {
    if (!key) return false;
    if (autoSendGuard.has(key)) {
        return true;
    }
    autoSendGuard.add(key);
    return false;
};

const recordAutoSend = (key: string): void => {
    if (!key) return;
    autoSendGuard.add(key);
};
type AdminMFAMethod = CodeSendingMethod | 'password';

interface OTPAuthStepsProps<OpenFileResult> {
    openFile: (code: string) => Promise<OpenFileResult>;
    closeOTP: SimpleCallback;
    stepsMounted: () => void;
    stepsInitialized: boolean;
}

const ADMIN_MESSAGE_DURATION = 30;

// `userStore` is populated asynchronously from the Cognito hub event, so
// `isUserSetUp` still reads false for a short window after a verification code is
// accepted. Anything that branches on the session (notably the password
// pre-check, which must not call Auth.signIn on an already-authenticated
// session) waits for it to settle - but never indefinitely.
const USER_SESSION_SETTLE_FALLBACK_MS = 5_000;

// eslint-disable-next-line @typescript-eslint/comma-dangle
const OTPAuthSteps = observer(<OpenFileResult,>({
    openFile, closeOTP, stepsInitialized, stepsMounted,
}: OTPAuthStepsProps<OpenFileResult>) => {
    const [currentStep, setCurrentStep] = useState<number | null>(null);
    const [showPhoneMissingModal, setShowPhoneMissingModal] = useState<boolean>(false);
    const [showPasswordMissingModal, setShowPasswordMissingModal] = useState<boolean>(false);
    const [hasPhone, setHasPhone] = useState<boolean>(false);
    const [hasPassword, setHasPassword] = useState<boolean>(false);
    const [codeError, setCodeError] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [wrongPasswordAttempts, setWrongPasswordAttempts] = useState<number>(3);
    const [textSource, setTextSource] = useState<'phone' | 'email' | null>(null);
    const [passwordHint, setPasswordHint] = useState<string | null>(null);
    // Track if phone code has been sent (for manual OTP mode when AUTOMATIC_OTP is false)
    const [isPhoneCodeSent, setIsPhoneCodeSent] = useState<boolean>(appConfig.AUTOMATIC_OTP !== false);
    const { t } = useTranslation();

    const newEmail = useRef<string>('');
    const isMFARequired = useRef<boolean>(false);
    const recipientPhone = useRef<PhoneIdentity | null>(null);
    // A link normally gets the benefit of the doubt on a phone the browser could not read, because a
    // bound share resolves the MFA destination server-side. That optimism must not survive a lookup
    // that came back empty for a cleartext address - there is nothing left for the link to resolve.
    const hasNoPhoneOnRecord = useRef<boolean>(false);
    const hasRedirectedToLogin = useRef<boolean>(false);
    const isStepMFAFlow = useRef<boolean>(false);

    const navigate = useNavigate();

    const {
        authSettingsStore,
        recipientPageStore: { sharedFilesStore, emailMessageStore },
        userStore,
    } = useStores();

    const {
        isAdmin, isCollaborator, isUserSetUp: isUserLoggedIn, currentUserEmail, authState,
    } = userStore;
    // Session restore is asynchronous on reload. Until `authState` leaves
    // `loading`, `isUserLoggedIn` is not trustworthy, so any auto-send decision
    // (phone/email/admin MFA) could fire against a stale, logged-out state and
    // send a duplicate verification code even though the recipient is already
    // verified. `useAuthResolved` also carries a timeout fallback so a session
    // that never settles can't leave the recipient permanently stuck.
    const authResolved = useAuthResolved(authState);

    const otpAdminsRestricted = typeof window !== 'undefined'
        ? sessionStorage.getItem(OTP_ADMINS_RESTRICTED) === 'true'
        : false;
    const otpCollaboratorsRestricted = typeof window !== 'undefined'
        ? sessionStorage.getItem(OTP_COLLABORATORS_RESTRICTED) === 'true'
        : false;

    const isStepMFA = (): boolean => isStepMFAFlow.current
        || (isUserLoggedIn && (isAdmin || (isCollaborator && otpCollaboratorsRestricted)));

    const {
        allFilesPoliciesAggregate,
        authRequirements,
        recipientEmail,
        maskedRecipientEmail,
        selectedFilePolicyAggregate,
        filesList,
        setIsMFARequired,
        isLoading,
        setIsContentLoading,
        senderEmail,
        allFilesCount,
        isMainDataLoaded,
        updateRequestsMap,
        anyRequestInProcess,
    } = sharedFilesStore;
    const recipientEmailHint = recipientEmail || maskedRecipientEmail;

    const {
        messageId,
    } = emailMessageStore;

    const {
        lastAccessType,
    } = useVerifyAction(sharedFilesStore, authSettingsStore.API);

    const policiesAggregate = selectedFilePolicyAggregate && lastAccessType !== 'batchDownload'
        ? selectedFilePolicyAggregate
        : allFilesPoliciesAggregate;

    const {
        email: hasEmailVerification,
        phone: hasPhoneVerification,
        password: hasPasswordProtection,
        standardLogin: requireStandardLogin,
    } = authRequirements;
    const hasAllowForwarding = !!policiesAggregate?.[ALLOW_FORWARDING];

    const { selectFile } = useFilesActionsContext();

    const {
        checkAuthCode,
        email,
        emailAuthFirstStepMessages,
        onSendEmailError,
        sendEmailAuthCode,
        sendAuthCode,
        resetEmail,
    } = useEmailOTPFlow(
        t,
        authSettingsStore,
        userStore,
        recipientEmail,
    );
    const startLoading = (): void => {
        setIsContentLoading(true);
    };

    const stopLoading = (): void => {
        setIsContentLoading(false);
    };

    // Resolves once the Cognito session has propagated into `userStore`, or after
    // a bounded fallback so a session that never settles cannot hang the flow.
    const waitForUserSession = async (): Promise<void> => {
        if (userStore.isUserSetUp) {
            return;
        }
        await Promise.race([
            userStore.subscribeOnceOnSetUser(),
            new Promise<void>((resolve) => {
                window.setTimeout(resolve, USER_SESSION_SETTLE_FALLBACK_MS);
            }),
        ]);
    };

    const { getLoginMethodPreference, setLoginMethodPreference, clearLoginMethodPreference } = useLoginMethodPreference();

    const redirectUnauthenticatedToLogin = async (emailForLogin?: string): Promise<void> => {
        // Never redirect a user who is already logged in to the sign-in page —
        // doing so while runSignedInUserFlow is active creates an infinite loop
        // (/shared-file → signIn → /shared-file → signIn → …).
        if (isUserLoggedIn || hasRedirectedToLogin.current) {
            return;
        }
        hasRedirectedToLogin.current = true;

        setLoginMethodPreference(LOGIN_METHOD_EMAIL);
        saveRedirectPath(`${window.location.pathname}${window.location.search}`);
        const resolvedEmail = firstCleartextEmail(emailForLogin, newEmail.current, recipientEmail);
        if (resolvedEmail) {
            sessionStorage.setItem(RECIPIENT_EMAIL_FOR_LOGIN, resolvedEmail);
            try {
                await trySignUp(resolvedEmail.toLowerCase(), authSettingsStore.API);
            } catch (error) {
                console.error('Failed to pre-provision Cognito user before login redirect:', error);
            }
        }
        navigate('/signIn');
    };

    const handlePhoneMissing = async (): Promise<void> => {
        // Sending the recipient to sign in can recover a phone the browser merely could not read,
        // but not one the auth service has no record of. Once the lookup has said so outright, the
        // fallback would only bounce them to a login that cannot help, so show the prompt instead.
        const loginFallbackCanHelp = appConfig.LOGIN_FALLBACK_ON_MISSING_PHONE
            && !isUserLoggedIn
            && !isStepMFAFlow.current
            && !hasNoPhoneOnRecord.current;

        if (loginFallbackCanHelp) {
            if (getLoginMethodPreference() === LOGIN_METHOD_OTP) {
                clearLoginMethodPreference();
                setShowPhoneMissingModal(true);
                return;
            }

            await redirectUnauthenticatedToLogin();
        } else {
            setShowPhoneMissingModal(true);
        }
    };

    const isNetworkRestrictionError = (error: unknown): boolean => (
        checkIsNetworkError(error)
        || (error as CognitoAuthError)?.code === CognitoErrorCodes.NetworkError
    );

    const isMissingPhoneError = (error: unknown): boolean => {
        const { statusCode, message: responseMessage } = getErrorResponse(error);
        const localMessage = error instanceof Error ? error.message : '';
        return localMessage === 'Phone number is missing'
            || (statusCode === 404 && /authy_user|phone/i.test(responseMessage || ''));
    };

    // The marker vouches only for the session that completed phone OTP. Without a session the
    // recipient needs a code to get back in, so a leftover marker must not suppress the send, and
    // it is dropped so a later email-only sign-in cannot inherit it.
    const resolvePhoneMfaDoneForLink = async (): Promise<boolean> => {
        const linkId = getLinkIdFromLocation();
        if (!isPhoneMfaCompletedForLink(linkId)) {
            return false;
        }
        if (await hasPlatformSession()) {
            return true;
        }
        clearPhoneMfaCompletedForLink(linkId);
        return false;
    };

    const handleNetworkRestriction = (error: unknown): void => {
        sharedFilesStore.setLinkErrorObject(error);
        sharedFilesStore.setErrorType('networkRestriction');
    };

    // Whether a link might still resolve a phone the browser itself could not read.
    const canLinkResolvePhone = (): boolean => (
        !!getLinkIdFromLocation() && !hasNoPhoneOnRecord.current
    );

    const checkPhoneAvailability = async (): Promise<void> => {
        updateRequestsMap('checkPhoneAvailability', true);
        try {
            let emailToCheck: string;
            if (isUserLoggedIn && hasEmailVerification && hasPhoneVerification) {
                emailToCheck = currentUserEmail;
            } else {
                emailToCheck = newEmail.current || email || recipientEmail || currentUserEmail;
            }

            // Bound masked links have no cleartext until generateCode/sendAuthCode.
            // Do not call generateCode here — that would mint an OTP solely for lookup;
            // sendAuthCode resolves email via linkId when sending the phone challenge.
            if (!emailToCheck || isMaskedEmail(emailToCheck)) {
                setHasPhone(false);
                return;
            }

            const response = await authSettingsStore.API.get(
                BASEURL.backend(),
                ENDPOINTS.getPhoneByEmail(emailToCheck),
                {},
            );
            if (response?.phone && response?.prefix) {
                const newRecipienPhone = { ...response };
                recipientPhone.current = ({ ...newRecipienPhone });
                hasNoPhoneOnRecord.current = false;
                setHasPhone(true);
                setShowPhoneMissingModal(false);
            } else {
                setHasPhone(false);
                // Bound links: Cognito challenge loads MFA phone by email — do not
                // block on a pre-auth getPhoneByEmail miss.
                if (
                    hasPhoneVerification
                    && (currentStep !== 0 || newEmail.current)
                    && !getLinkIdFromLocation()
                ) {
                    await handlePhoneMissing();
                }
            }
        } catch (error) {
            console.error('Error checking phone availability:', error);
            setHasPhone(false);

            // The lookup answers "no phone" with a 404 carrying an authy_user message, the same
            // signal the send paths already treat as missing. That is a definitive verdict about a
            // cleartext address, so prompt instead of letting the link carry the flow into a
            // verification step with nowhere to send the code.
            if (isMissingPhoneError(error)) {
                hasNoPhoneOnRecord.current = true;
                if (hasPhoneVerification) {
                    setShowPhoneMissingModal(true);
                }
                return;
            }

            // Any other failure (network, auth) says nothing about the phone, so this keeps the
            // conservative handling - including the per-stage login fallback.
            if (
                hasPhoneVerification
                && (currentStep !== 0 || newEmail.current)
                && !getLinkIdFromLocation()
            ) {
                await handlePhoneMissing();
            }
        } finally {
            updateRequestsMap('checkPhoneAvailability', false);
        }
    };

    const sendAdminMFACode = async (method: AdminMFAMethod): Promise<void> => {
        try {
            const queryParams = new URLSearchParams(window.location.search);
            const linkId = caseInsensitiveUrlParam(queryParams, 'linkid');

            await authSettingsStore.API.post(
                BASEURL.backend(),
                ENDPOINTS.sendAuthStep(),
                {
                    response: true,
                    body: { linkId, method },
                },
            );
        } catch (error) {
            console.error('Error sending admin MFA code:', error);
            throw error;
        }
    };

    const handleDidMountedError = async (error) => {
        const { message: errorDescription } = getErrorResponse(error);
        const errorType = parseAuthErrorType(errorDescription);
        if (errorType === 'adminAccessDenied' || errorType === 'collaboratorsAccessDenied') {
            if (typeof window !== 'undefined') {
                if (errorType === 'adminAccessDenied') {
                    sessionStorage.setItem(OTP_ADMINS_RESTRICTED, 'true');
                }
                if (errorType === 'collaboratorsAccessDenied') {
                    sessionStorage.setItem(OTP_ADMINS_RESTRICTED, 'true');
                    sessionStorage.setItem(OTP_COLLABORATORS_RESTRICTED, 'true');
                }
            }
            message.info(i18n.t(`${nameSpace}.OTPSteps.${errorType}`), ADMIN_MESSAGE_DURATION);
            await redirectUnauthenticatedToLogin();
        } else if (errorType === 'signUpForbidden') {
            message.info(i18n.t('codeAuth.firstStepCommon.signUpForbidden'), ADMIN_MESSAGE_DURATION);
        }
    };

    // Session state is read live rather than from the render closure: this runs
    // from async continuations (post code-verification) where the captured
    // `isUserLoggedIn` is stale.
    const getPasswordCheckUserIdentifier = (fallbackEmail?: string): string | null => {
        if (userStore.isUserSetUp) {
            return userStore.currentUserEmail;
        }

        return firstCleartextEmail(newEmail.current, email, recipientEmail, fallbackEmail) || null;
    };

    const checkPasswordAvailability = async (fallbackEmail?: string): Promise<boolean> => {
        // If phone verification is required and we still don't have a phone identity,
        // prioritise phone error and skip password check for now.
        // For logged-in admins / restricted collaborators the phone is fetched best-effort;
        // the backend handles auth via linkId so we must not block the password step here.
        // Live read, not the render closure: after a phone code is accepted this
        // helper runs from an async continuation whose captured `isUserLoggedIn`
        // predates the Cognito session (SPX-6543). Taking the signed-out branch
        // there calls Auth.signIn on an already-authenticated session and the
        // password step is never reached.
        const isSessionEstablished = userStore.isUserSetUp;

        const isAdminMFAFlow = isStepMFAFlow.current
            || (isSessionEstablished && (isAdmin || (isCollaborator && otpCollaboratorsRestricted)));
        if (hasPhoneVerification && !recipientPhone.current?.phone && !isAdminMFAFlow) {
            return false;
        }

        const userIdentifier = getPasswordCheckUserIdentifier(fallbackEmail);

        if (!userIdentifier) {
            return false;
        }

        updateRequestsMap('checkPasswordAvailability', true);
        try {
            // Prefer the org id from the loaded link data (kept in-memory) over the
            // volatile localStorage key: the Cognito `signedIn` event fired after the
            // email step runs AppAuthenticator.trySetUpInCognitoEnv, whose catch calls
            // organizationsStore.clear() and wipes CURRENT_ORGANIZATION for recipients
            // who aren't full app users. Without a fallback the password check would
            // omit ?organization_id= and the backend 400s (caller_id could not be None).
            const organizationId = localStorage.getItem(CURRENT_ORGANIZATION)
                || sharedFilesStore.linkData?.organization_id
                || undefined;

            const response = await authSettingsStore.API.get(
                BASEURL.backend(),
                ENDPOINTS.getUserPasswordPublic(userIdentifier, organizationId),
                {},
            );

            // Save hint from response, fallback to config default
            const hint = response?.hint || appConfig.PASSWORD_DEFAULT_HINT || null;
            setPasswordHint(hint);
            sharedFilesStore.setPasswordHint(hint);

            // Initialize MFA session for password verification.
            // All logged-in users use the backend MFA step flow (sendAdminMFACode / verifyAdminMFA)
            // because their Cognito session is already established and calling Auth.signIn again
            // would throw a "User already authenticated" error.
            if (isSessionEstablished) {
                await sendAdminMFACode('password');
            } else {
                await sendAuthCode(userIdentifier, 'password');
            }

            setHasPassword(true);
            setShowPasswordMissingModal(false);
            return true;
        } catch (error) {
            console.error('Error checking password availability:', error);
            if (!isSessionEstablished) {
                const { message: errorMessage } = getErrorResponse(error);
                if (errorMessage && parseAuthErrorType(errorMessage)) {
                    await handleDidMountedError(error);
                    return false;
                }
            }
            setHasPassword(false);
            if (hasPasswordProtection) {
                setShowPasswordMissingModal(true);
            }
            return false;
        } finally {
            updateRequestsMap('checkPasswordAvailability', false);
        }
    };

    const getStepIndices = () => {
        let currentIndex = 0;
        const emailStep = currentIndex++;
        const emailVerificationStep = hasEmailVerification ? currentIndex++ : -1;
        const phoneVerificationStep = hasPhoneVerification ? currentIndex++ : -1;
        const passwordStep = hasPasswordProtection ? currentIndex++ : -1;

        return {
            emailStep,
            passwordStep,
            emailVerificationStep,
            phoneVerificationStep,
        };
    };

    const goToPasswordStep = (): void => {
        // Password verification doesn't require sending anything to the user
        // Just transition to the password step where user enters their password
        // Hint is already fetched in checkPasswordAvailability
        setCurrentStep(getStepIndices().passwordStep);
    };

    const checkPhoneAndSendCode = async (): Promise<void> => {
        updateRequestsMap('checkPhoneAndSendCode', true);
        const indices = getStepIndices();
        try {
            const isAdminMFA = isStepMFA();
            // sendAdminMFACode uses the linkId (sendAuthStep endpoint) and does not require
            // a phone number fetched via getPhoneByEmail. Skip the guard for admin MFA so
            // the flow can proceed even when the backend blocked getPhoneByEmail with 403.
            if (!isAdminMFA && (!recipientPhone.current?.phone || !recipientPhone.current?.prefix)) {
                await checkPhoneAvailability();
            }
            if (!isAdminMFA && (!recipientPhone.current?.phone || !recipientPhone.current?.prefix)) {
                // Bound share: create_auth_challenge loads MFA phone by email. Allow
                // send without FE phone when linkId can resolve the recipient.
                if (!canLinkResolvePhone()) {
                    throw new Error('Phone number is missing');
                }
            }

            if (isAdminMFA) {
                await sendAdminMFACode('phone');
            } else {
                let emailToUse: string;
                if (isUserLoggedIn && hasEmailVerification && hasPhoneVerification) {
                    emailToUse = currentUserEmail;
                } else {
                    emailToUse = newEmail.current || recipientEmailHint || currentUserEmail || '';
                }
                const resolvedEmail = await sendAuthCode(
                    emailToUse,
                    'phone',
                    recipientPhone.current || undefined,
                );
                newEmail.current = resolvedEmail;
            }
            setCurrentStep(indices.phoneVerificationStep);
        } catch (error) {
            if (isNetworkRestrictionError(error)) {
                handleNetworkRestriction(error);
                return;
            }
            console.error('Error checking phone and sending code:', error);
            // Only show the missing-phone modal for an explicit FE guard failure or the
            // backend's definitive "no phone on record" 404. Bound links often have no FE
            // phone identity; Cognito/network errors must not be mis-labeled as missing.
            // Step MFA skips the FE guard, so the 404 is its only missing-phone signal.
            if (isMissingPhoneError(error)) {
                await handlePhoneMissing();
            }
            await handleDidMountedError(error);
        } finally {
            updateRequestsMap('checkPhoneAndSendCode', false);
        }
    };

    const checkPhoneAndSendVoiceCode = async (): Promise<void> => {
        updateRequestsMap('checkPhoneAndSendVoiceCode', true);
        const indices = getStepIndices();
        try {
            if (!recipientPhone.current?.phone || !recipientPhone.current?.prefix) {
                await checkPhoneAvailability();
            }
            if (!recipientPhone.current?.phone || !recipientPhone.current?.prefix) {
                if (!canLinkResolvePhone()) {
                    throw new Error('Phone number is missing');
                }
            }

            if (isStepMFA()) {
                await sendAdminMFACode('voice');
            } else {
                let emailToUse: string;
                if (isUserLoggedIn && hasEmailVerification && hasPhoneVerification) {
                    emailToUse = currentUserEmail;
                } else {
                    emailToUse = newEmail.current || recipientEmailHint || currentUserEmail || '';
                }
                const resolvedEmail = await sendAuthCode(
                    emailToUse,
                    'voice',
                    recipientPhone.current || undefined,
                );
                newEmail.current = resolvedEmail;
            }
            setCurrentStep(indices.phoneVerificationStep);
        } catch (error) {
            if (isNetworkRestrictionError(error)) {
                handleNetworkRestriction(error);
                return;
            }
            console.error('Error sending voice code:', error);
            if (isMissingPhoneError(error)) {
                await handlePhoneMissing();
            }
            await handleDidMountedError(error);
        } finally {
            updateRequestsMap('checkPhoneAndSendVoiceCode', false);
        }
    };

    const checkPermissionsAfterAuth = async (): Promise<void> => {
        try {
            await userStore.subscribeOnceOnSetUser();
            // File metadata is deferred until the recipient is authenticated;
            // fetch it now (idempotent) before adding permissions.
            await sharedFilesStore.ensureFilesLoaded();
            await sharedFilesStore.tryBatchAddPermissions({ isSilent: false });
        } catch (error) {
            console.error('Error checking permissions after auth:', error);
        }
    };

    type MFARequirement = MfaProbeResult;

    const verifyAdminMFA = async (method: AdminMFAMethod, attempt: string): Promise<void> => {
        const verifyMethod = method === 'password' ? 'password' : 'phone';
        const response = await authSettingsStore.API.post(
            BASEURL.backend(),
            ENDPOINTS.verifyFileAccess(),
            {
                body: {
                    method: verifyMethod,
                    attempt,
                },
            },
        );

        if (!response?.success) {
            throw new Error('MFA verification failed');
        }
    };

    const checkIsMFARequired = async (): Promise<MFARequirement> => {
        // Live read, not the render closure: callers load metadata immediately
        // before probing, and a closure captured at mount still holds the empty
        // pre-auth list, which would short-circuit every probe to 'unknown'.
        const files = sharedFilesStore.filesList;
        // Pre-auth the file list is intentionally empty (metadata is deferred),
        // so there is nothing to probe. The auth-flow-derived phone/password
        // requirements drive the flow instead; skip the file-level probe to
        // avoid issuing a pre-auth file-open request.
        if (!files.length) {
            return 'unknown';
        }
        updateRequestsMap('checkIsMFARequired', true);
        try {
            const filesWithMfa = files.find((item) => item.policy.isMfa)?.file_id;
            const filesWithPassword = files.find((item) => item?.policy?.settings?.access?.some(
                (setting) => setting.id === REQUIRED_PASSWORD_VERIFICATION && setting.value === true,
            ),
            )?.file_id;
            const body = {
                app_name: OpenOptions.viewer,
                always_check_mfa: true,
            };

            const result = await authSettingsStore.API.post(
                BASEURL.backend(),
                ENDPOINTS.openFile(filesWithMfa || filesWithPassword),
                { body },
            );
            if (result) {
                // Do not clear sharedFilesStore.isMFARequired here. After email Cognito
                // auth, open can succeed before phone/password auth-flow steps finish;
                // clearing MFA would let LinkView authorize and unmount OTP. Callers that
                // complete the full auth-flow clear the flag explicitly.
                return 'none';
            }
        } catch (error) {
            const { statusCode, message: errorMessage } = getErrorResponse(error);
            // Use shared helpers so behavior matches the rest of the app
            if (checkIsMFAPasswordRequiredAPI(statusCode, errorMessage)) {
                setIsMFARequired(true);
                isMFARequired.current = true;
                return 'password';
            }
            if (
                checkIsMFARequiredAPI(statusCode, errorMessage)
                || (hasAllowForwarding && checkMessageContainsPermissions(errorMessage))
            ) {
                setIsMFARequired(true);
                isMFARequired.current = true;
                return 'phone';
            }
            if (checkMessageContainsPermissions(errorMessage)) {
                sharedFilesStore.setErrorType('noPermissions');
                closeOTP();
            }
            // Unrecognised failure: the probe says nothing about the policy.
            return 'unknown';
        } finally {
            updateRequestsMap('checkIsMFARequired', false);
        }
        // Open returned a falsy body without throwing.
        return 'unknown';
    };

    useEffect(() => {
        async function fetchData() {
            const initialUserEmail = isUserLoggedIn ? currentUserEmail : recipientEmailHint;
            const shouldStartEmailFlow = !!initialUserEmail && hasEmailVerification && !isUserLoggedIn;
            const shouldStartPhoneFlow = (!!initialUserEmail || isUserLoggedIn) && hasPhoneVerification;
            const shouldStartPasswordFlow = (!!initialUserEmail || isUserLoggedIn) && hasPasswordProtection;
            // Email Cognito alone can make /open succeed; still require phone when
            // auth-flow asks for it and this session has not completed phone OTP.
            const phoneMfaDoneForLink = await resolvePhoneMfaDoneForLink();
            const isMfaRequiredByDefault = hasPhoneVerification && !phoneMfaDoneForLink;
            const isPasswordRequiredByDefault = hasPasswordProtection && !isUserLoggedIn;
            const indices = getStepIndices();
            let handedOffToLinkView = false;

            try {
                // SPX-6391: frequent users skip recipient OTP and use Cognito /signIn.
                if (requireStandardLogin && !isUserLoggedIn) {
                    await redirectUnauthenticatedToLogin(initialUserEmail || recipientEmail);
                    return;
                }

                // 1) No user email known yet → ask for email
                if (!isUserLoggedIn && !initialUserEmail) {
                    setCurrentStep(indices.emailStep);
                    // 2) No email / phone verification configured
                } else if (!shouldStartEmailFlow && !shouldStartPhoneFlow) {
                    // If password protection is enabled and we know the email, go directly to password step
                    if (hasPasswordProtection && initialUserEmail) {
                        const passwordExists = await checkPasswordAvailability(initialUserEmail);
                        if (passwordExists) {
                            await checkIsMFARequired();
                            goToPasswordStep();
                        }
                    } else if (!isUserLoggedIn && !initialUserEmail) {
                        // Only show "enter email" when user is not logged in AND we don’t know recipient email
                        setCurrentStep(indices.emailStep);
                    }
                    // 3) Email verification flow
                } else if (shouldStartEmailFlow) {
                    // If password protection is also enabled, check whether backend expects
                    // only password MFA and skip email verification in that case.
                    if (hasPasswordProtection) {
                        const mfaRequirement = await checkIsMFARequired();
                        if (mfaRequirement === 'password') {
                            const passwordExists = await checkPasswordAvailability(initialUserEmail);
                            if (passwordExists) {
                                goToPasswordStep();
                                return;
                            }
                        }
                    }
                    // When AUTOMATIC_OTP is false, show email step with pre-filled email
                    // so user can click "Send code" manually
                    if (appConfig.AUTOMATIC_OTP === false) {
                        newEmail.current = initialUserEmail;
                        setCurrentStep(indices.emailStep);
                    } else if (!shouldSkipAutoSend(`email:${initialUserEmail}`)) {
                        if (document.visibilityState === 'visible') {
                            const resolvedEmail = await sendEmailAuthCode(initialUserEmail);
                            newEmail.current = resolvedEmail;
                            setCurrentStep(indices.emailVerificationStep);
                        } else {
                            newEmail.current = initialUserEmail;
                            setCurrentStep(indices.emailStep);
                        }
                    } else {
                        newEmail.current = initialUserEmail;
                        setCurrentStep(indices.emailStep);
                    }
                } else if (shouldStartPhoneFlow) {
                    isStepMFAFlow.current = await hasPlatformSession();
                    const isAdminMFA = isStepMFA();
                    if (isAdminMFA) {
                        await sharedFilesStore.ensureFilesLoaded();
                        const gate = resolvePhoneGate(await checkIsMFARequired(), hasPasswordProtection);
                        if (gate === 'skip') {
                            handedOffToLinkView = true;
                            markPhoneMfaCompletedForLink(getLinkIdFromLocation());
                            setIsMFARequired(false);
                            isMFARequired.current = false;
                            await checkPermissionsAfterAuth();
                            return;
                        }
                        if (gate === 'password') {
                            const passwordExists = await checkPasswordAvailability(initialUserEmail);
                            if (passwordExists) {
                                goToPasswordStep();
                            }
                            return;
                        }
                        try {
                            const phoneResponse = await authSettingsStore.API.get(
                                BASEURL.backend(),
                                ENDPOINTS.getPhoneByEmail(currentUserEmail),
                                {},
                            );
                            if (phoneResponse?.phone && phoneResponse?.prefix) {
                                recipientPhone.current = { phone: phoneResponse.phone, prefix: phoneResponse.prefix };
                                setHasPhone(true);
                            }
                        } catch (phoneErr) {
                            console.warn('Could not fetch admin phone for display:', phoneErr);
                        }

                        if (appConfig.AUTOMATIC_OTP === false) {
                            setIsPhoneCodeSent(false);
                            setCurrentStep(indices.phoneVerificationStep);
                        } else if (!shouldSkipAutoSend(`phone:${initialUserEmail}`)) {
                            if (document.visibilityState === 'visible') {
                                await checkPhoneAndSendCode();
                                setIsPhoneCodeSent(true);
                            } else {
                                setIsPhoneCodeSent(false);
                                setCurrentStep(indices.phoneVerificationStep);
                            }
                        } else {
                            setCurrentStep(indices.phoneVerificationStep);
                            setIsPhoneCodeSent(true);
                        }
                    } else {
                        await checkPhoneAvailability();
                        const mfaRequirement = await checkIsMFARequired();
                        if (mfaRequirement === 'phone' || (mfaRequirement !== 'password' && isMfaRequiredByDefault)) {
                            setIsMFARequired(true);
                            isMFARequired.current = true;
                            const phoneAvailable = !!(recipientPhone.current?.phone && recipientPhone.current?.prefix);
                            // Bound links may lack FE phone until Cognito challenge; still send.
                            if (!phoneAvailable && !canLinkResolvePhone()) {
                                await handlePhoneMissing();
                            } else if (appConfig.AUTOMATIC_OTP === false) {
                                setIsPhoneCodeSent(false);
                                setCurrentStep(indices.phoneVerificationStep);
                            } else {
                                await checkPhoneAndSendCode();
                                setIsPhoneCodeSent(true);
                            }
                        } else if (mfaRequirement === 'password' && hasPasswordProtection) {
                            const passwordExists = await checkPasswordAvailability(initialUserEmail);
                            if (passwordExists) {
                                goToPasswordStep();
                                stopLoading();
                            }
                        }
                    }
                } else if (shouldStartPasswordFlow) {
                    const passwordExists = await checkPasswordAvailability(initialUserEmail);
                    if (passwordExists) {
                        if (!isPasswordRequiredByDefault) {
                            await checkIsMFARequired();
                        }
                        setCurrentStep(indices.passwordStep);
                    }
                }
            } catch (error) {
                if (error instanceof InvalidLinkError) {
                    // When forwarding is disallowed by policy, a signup rejection for an
                    // unlisted recipient email is expected behavior, not a broken link —
                    // show "you need access" rather than "invalid link".
                    sharedFilesStore.setErrorType(hasAllowForwarding ? 'invalidLink' : 'noPermissions');
                    if (!hasAllowForwarding) {
                        closeOTP();
                    }
                    return;
                }
                if (isNetworkRestrictionError(error)) {
                    handleNetworkRestriction(error);
                    return;
                }
                if (!isUserLoggedIn) {
                    await handleDidMountedError(error);
                }
            } finally {
                if (isMainDataLoaded) {
                    stopLoading();
                }
                // SPX-7023: a null currentStep renders an indefinite spinner, and some paths above
                // settle without picking a step (e.g. the code send fails). Land on a step the
                // recipient can act on: a signed-in recipient resends from the phone step, anyone
                // else restarts resolution from the email step.
                if (!handedOffToLinkView && !sharedFilesStore.errorType) {
                    const fallbackStep = isStepMFAFlow.current && hasPhoneVerification
                        ? indices.phoneVerificationStep
                        : indices.emailStep;
                    setCurrentStep((step) => step ?? fallbackStep);
                }
            }
        }
        if (isMainDataLoaded && authResolved) {
            fetchData();
            stepsMounted();
        }
    }, [isMainDataLoaded, authResolved]);

    const handleEmailSubmit = async (userEmail: string) => {
        startLoading();
        try {
            recipientPhone.current = null;
            // A different address is a different recipient, so the previous "no phone" verdict no
            // longer applies and the link regains the benefit of the doubt until the next lookup.
            hasNoPhoneOnRecord.current = false;
            setHasPhone(false);

            const phoneMfaDoneForLink = await resolvePhoneMfaDoneForLink();
            const isMfaRequiredByDefault = hasPhoneVerification && !phoneMfaDoneForLink;
            const indices = getStepIndices();

            if (hasEmailVerification) {
                // Resolve / echo typed cleartext before any getPhoneByEmail call.
                updateRequestsMap('sendEmailAuthCode', true);
                try {
                    const resolvedEmail = await sendEmailAuthCode(userEmail);
                    newEmail.current = resolvedEmail;
                    recordAutoSend(`email:${resolvedEmail}`);
                    if (hasPhoneVerification) {
                        await checkPhoneAvailability();
                    }
                    setCurrentStep(indices.emailVerificationStep);
                } finally {
                    updateRequestsMap('sendEmailAuthCode', false);
                }
            } else if (hasPhoneVerification && !hasEmailVerification) {
                // Stash typed cleartext so getPhoneByEmail can run.
                if (userEmail && !isMaskedEmail(userEmail)) {
                    newEmail.current = userEmail;
                }
                await checkPhoneAvailability();
                // Always check with backend what verification is still needed
                const mfaRequirement = await checkIsMFARequired();
                // If backend says phone is needed, or if we couldn't determine (fallback to policy)
                if (mfaRequirement === 'phone' || (mfaRequirement !== 'password' && isMfaRequiredByDefault)) {
                    await checkPhoneAndSendCode();
                    setCurrentStep(indices.phoneVerificationStep);
                } else if (mfaRequirement === 'password' && hasPasswordProtection) {
                    const emailForPassword = userEmail || newEmail.current;
                    const passwordExists = await checkPasswordAvailability(emailForPassword);
                    if (passwordExists) {
                        goToPasswordStep();
                        stopLoading();
                    }
                }
            } else if (hasPasswordProtection && !hasPhoneVerification && !hasEmailVerification) {
                const passwordExists = await checkPasswordAvailability(userEmail);
                if (passwordExists) {
                    goToPasswordStep();
                }
            }
        } catch (error) {
            if (error instanceof InvalidLinkError) {
                // Same distinction as the mount-time flow: a signup rejection caused by
                // a no-forwarding policy means "you need access", not "invalid link".
                sharedFilesStore.setErrorType(hasAllowForwarding ? 'invalidLink' : 'noPermissions');
                if (!hasAllowForwarding) {
                    closeOTP();
                }
                return;
            }
            if (isNetworkRestrictionError(error)) {
                handleNetworkRestriction(error);
                return;
            }
            console.error('Error submitting email:', error);
            const { message: errorMessage } = getErrorResponse(error);
            const restrictedRoleError = parseAuthErrorType(errorMessage);
            await handleDidMountedError(error);
            if (restrictedRoleError !== 'adminAccessDenied' && restrictedRoleError !== 'collaboratorsAccessDenied') {
                onSendEmailError(error);
            }
        } finally {
            stopLoading();
        }
    };

    const handleCodeSubmit = async (code: string) => {
        startLoading();
        const indices = getStepIndices();
        try {
            // Assert MFA before checkAuthCode: that call sets isUserSetUp and can
            // race LinkView into closeOTP while isMFARequired is still false.
            if (
                (currentStep === indices.emailVerificationStep && hasPhoneVerification)
                || (currentStep === indices.emailVerificationStep && hasPasswordProtection)
                || (currentStep === indices.phoneVerificationStep && hasPasswordProtection)
            ) {
                setIsMFARequired(true);
                isMFARequired.current = true;
            }
            // Must mirror the send-side predicate in checkPhoneAndSendCode: a code minted
            // via /step/send is only redeemable via /step/verify, and one minted through
            // the Cognito challenge only via confirmSignIn. If the two disagree the code is
            // submitted to the channel that never issued it and fails as "wrong code".
            if (isStepMFA()) {
                await verifyAdminMFA('phone', code);
            } else {
                await checkAuthCode(
                    code,
                    () => { updateRequestsMap('checkAuthCode', true); },
                    () => { updateRequestsMap('checkAuthCode', false); },
                    () => { },
                );
            }
            // The recipient is now authenticated (Cognito custom-auth completed),
            // but `userStore` is filled in asynchronously from the hub event. Let
            // it settle before any branch that reads the session, otherwise the
            // password pre-check below runs as if the recipient were still signed
            // out (SPX-6543).
            await waitForUserSession();
            await sharedFilesStore.ensureFilesLoaded();
            if (currentStep === indices.emailVerificationStep && hasPhoneVerification) {
                await checkPhoneAvailability();
                // Use ref for immediate check since state updates are async
                const phoneAvailable = !!(recipientPhone.current?.phone && recipientPhone.current?.prefix);
                const mfaRequirement = await checkIsMFARequired();
                // Always advance when auth-flow still requires phone. Do not gate
                // on stepsInitialized — that flag can lag and previously skipped
                // phone after email Cognito while OTP stayed on the email step.
                if (mfaRequirement === 'password' && hasPasswordProtection) {
                    // Password takes precedence if explicitly required by backend
                    const passwordExists = await checkPasswordAvailability();
                    if (passwordExists) {
                        goToPasswordStep();
                    } else if (phoneAvailable || canLinkResolvePhone()) {
                        // Password probe won but no password on file — still do phone.
                        // Bound links may lack FE phone until Cognito challenge.
                        if (appConfig.AUTOMATIC_OTP === false) {
                            setIsPhoneCodeSent(false);
                            setCurrentStep(getStepIndices().phoneVerificationStep);
                        } else {
                            await checkPhoneAndSendCode();
                            setIsPhoneCodeSent(true);
                        }
                    } else {
                        await handlePhoneMissing();
                    }
                    stopLoading();
                } else if (phoneAvailable || canLinkResolvePhone()) {
                    // Proceed to phone verification since policy requires it
                    if (appConfig.AUTOMATIC_OTP === false) {
                        // Go to phone step without sending code
                        setIsPhoneCodeSent(false);
                        setCurrentStep(getStepIndices().phoneVerificationStep);
                    } else {
                        // checkPhoneAndSendCode sets the step internally
                        await checkPhoneAndSendCode();
                        setIsPhoneCodeSent(true);
                    }
                    stopLoading();
                } else {
                    // Phone required but unavailable — surface missing-phone UX.
                    await handlePhoneMissing();
                    stopLoading();
                }
            } else if (
                currentStep === indices.emailVerificationStep
                && !hasPhoneVerification
                && hasPasswordProtection
            ) {
                const passwordExists = await checkPasswordAvailability();
                if (passwordExists) {
                    goToPasswordStep();
                    stopLoading();
                } else {
                    stopLoading();
                }
            } else if (currentStep === indices.phoneVerificationStep && hasPasswordProtection) {
                markPhoneMfaCompletedForLink(getLinkIdFromLocation());
                const passwordExists = await checkPasswordAvailability();
                if (passwordExists) {
                    goToPasswordStep();
                }
                // Unconditional: `checkPasswordAvailability` reports its own
                // failure (PasswordMissingModal or a routed auth error), so a
                // false return must still clear the loader instead of leaving the
                // recipient on an indefinite skeleton with no way forward.
                stopLoading();
            } else if (allFilesCount === 1 && !messageId) {
                if (currentStep === indices.phoneVerificationStep) {
                    markPhoneMfaCompletedForLink(getLinkIdFromLocation());
                }
                setIsMFARequired(false);
                isMFARequired.current = false;
                // Always add permissions after auth-flow completes. Bound/masked
                // links set newEmail from generateCode; skipping this left
                // accessibleFiles empty so the files row showed no attachments.
                await checkPermissionsAfterAuth();
                const MAX_AUTO_OPEN_SIZE = 30 * 1024 * 1024;
                let fileTooLarge = false;
                try {
                    const file = filesList[0];
                    if (isOfficeFile(file.filename)) {
                        const fileDetails = await getFile(authSettingsStore.API, file.file_id);
                        if ((fileDetails.file_size ?? 0) > MAX_AUTO_OPEN_SIZE) {
                            fileTooLarge = true;
                        }
                    }
                    if (!fileTooLarge) {
                        selectFile(file.file_id);
                        await openFile(code);
                    }
                } catch {
                    selectFile(filesList[0].file_id);
                    await openFile(code);
                }
                stopLoading();
                closeOTP();
            } else {
                if (currentStep === indices.phoneVerificationStep) {
                    markPhoneMfaCompletedForLink(getLinkIdFromLocation());
                }
                setIsMFARequired(false);
                isMFARequired.current = false;
                await checkPermissionsAfterAuth();
                stopLoading();
                closeOTP();
            }
            setCodeError(false);
        } catch (error) {
            console.error('Error verifying code:', error);
            const { message: errorMessage } = getErrorResponse(error);
            if (errorMessage) {
                const isPermissionError = checkMessageContainsPermissions(errorMessage)
                    || errorMessage.includes('Abort MFA flow')
                    || errorMessage.includes("don't have permission");
                if (isPermissionError) {
                    sharedFilesStore.setErrorType('noPermissions');
                    closeOTP();
                } else {
                    setCodeError(true);
                }
            } else {
                setCodeError(true);
            }
            stopLoading();
        }
    };

    const handleResendCode = async (method: CodeSendingMethod) => {
        startLoading();
        updateRequestsMap('handleResendCode', true);
        const indices = getStepIndices();
        try {
            if (currentStep === indices.emailVerificationStep) {
                const emailToUse = newEmail.current || recipientEmailHint || email;
                const resolvedEmail = await sendEmailAuthCode(emailToUse);
                newEmail.current = resolvedEmail;
                recordAutoSend(`email:${resolvedEmail}`);
            } else if (currentStep === indices.phoneVerificationStep) {
                if (!recipientPhone.current?.phone || !recipientPhone.current?.prefix) {
                    await checkPhoneAvailability();
                }
                if (!recipientPhone.current?.phone || !recipientPhone.current?.prefix) {
                    if (!canLinkResolvePhone()) {
                        throw new Error('Phone number is missing');
                    }
                }
                let emailToUse: string;
                if (isStepMFA()) {
                    await sendAdminMFACode(method);
                } else {
                    if (isUserLoggedIn && hasEmailVerification && hasPhoneVerification) {
                        emailToUse = currentUserEmail;
                    } else {
                        emailToUse = newEmail.current || recipientEmailHint || '';
                    }
                    const resolvedEmail = await sendAuthCode(
                        emailToUse,
                        method,
                        recipientPhone.current || undefined,
                    );
                    newEmail.current = resolvedEmail;
                    recordAutoSend(`phone:${resolvedEmail || emailToUse}`);
                }
            }
        } catch (error) {
            if (isNetworkRestrictionError(error)) {
                handleNetworkRestriction(error);
                return;
            }
            console.error('Error resending code:', error);
            if (currentStep === indices.emailVerificationStep) {
                onSendEmailError(error);
            } else if (currentStep === indices.phoneVerificationStep) {
                const errorMessage = (error as any)?.message || (error as any)?.response?.data?.message || '';
                if (errorMessage === 'Phone number is missing') {
                    await handlePhoneMissing();
                } else {
                    message.error(i18n.t(`${nameSpace}.OTPSteps.errorResendingCode`));
                }
            }
        } finally {
            stopLoading();
            updateRequestsMap('handleResendCode', false);
        }
    };

    const handlePasswordSubmit = async (password: string) => {
        if (!wrongPasswordAttempts) {
            return;
        }

        startLoading();
        const initialUserEmail = isUserLoggedIn ? currentUserEmail : recipientEmail;
        try {
            if (isUserLoggedIn) {
                // All logged-in users verify via the backend MFA step endpoint, because
                // signedInUserRef is not populated for already-authenticated sessions.
                await verifyAdminMFA('password', password);
            } else {
                await checkAuthCode(
                    password,
                    () => { updateRequestsMap('checkAuthCode', true); },
                    () => { updateRequestsMap('checkAuthCode', false); },
                    () => { },
                );
            }
            setIsMFARequired(false);
            isMFARequired.current = false;
            // Authenticated via password: fetch the deferred file metadata
            // before the single/multi-file auto-open branch reads filesList.
            await sharedFilesStore.ensureFilesLoaded();
            await checkPermissionsAfterAuth();
            if (allFilesCount === 1 && !messageId) {
                const MAX_AUTO_OPEN_SIZE = 30 * 1024 * 1024;
                let fileTooLarge = false;
                try {
                    const file = filesList[0];
                    if (isOfficeFile(file.filename)) {
                        const fileDetails = await getFile(authSettingsStore.API, file.file_id);
                        if ((fileDetails.file_size ?? 0) > MAX_AUTO_OPEN_SIZE) {
                            fileTooLarge = true;
                        }
                    }
                    if (!fileTooLarge) {
                        selectFile(file.file_id);
                        await openFile(password);
                    }
                } catch {
                    selectFile(filesList[0].file_id);
                    await openFile(password);
                }
                stopLoading();
                closeOTP();
            } else {
                stopLoading();
                closeOTP();
            }
            setPasswordError(false);
            setWrongPasswordAttempts(3);
        } catch (error) {
            console.error('Error in password submit:', error);
            const { message: errorMessage } = getErrorResponse(error);
            // For external users, route admin/collaborator OTP restriction errors
            // through the shared handler so behavior matches phone/email flows.
            if (!isUserLoggedIn && errorMessage && parseAuthErrorType(errorMessage)) {
                await handleDidMountedError(error);
                stopLoading();
                return;
            }
            if (errorMessage) {
                const isPermissionError = checkMessageContainsPermissions(errorMessage)
                    || errorMessage.includes('Abort MFA flow')
                    || errorMessage.includes("don't have permission");
                if (isPermissionError) {
                    sharedFilesStore.setErrorType('noPermissions');
                    closeOTP();
                } else {
                    setPasswordError(true);
                    setWrongPasswordAttempts((prev) => (prev > 0 ? prev - 1 : 0));
                }
            } else {
                setPasswordError(true);
                setWrongPasswordAttempts((prev) => (prev > 0 ? prev - 1 : 0));
            }
            stopLoading();
        }
    };

    const stepsVisible = (): boolean => {
        const ifEmailExist = !!recipientEmailHint;
        // Show steps if we have password protection OR if email/phone verification match
        const hasMultipleSteps = hasPasswordProtection
            || hasEmailVerification
            || hasPhoneVerification;

        // Original logic: hide steps if email exists and only one verification type is enabled (no password)
        const shouldHideForSingleVerification = ifEmailExist
            && hasEmailVerification !== hasPhoneVerification
            && !hasPasswordProtection;

        return hasMultipleSteps && !shouldHideForSingleVerification;
    };

    const stepIndices = getStepIndices();

    const steps = [
        { title: t(`${nameSpace}.OTPSteps.enterEmail`) },
        ...(hasEmailVerification ? [{ title: t(`${nameSpace}.OTPSteps.verifyEmail`) }] : []),
        ...(hasPhoneVerification ? [{ title: t(`${nameSpace}.OTPSteps.verifyPhone`) }] : []),
        ...(hasPasswordProtection ? [{ title: t(`${nameSpace}.OTPSteps.verifyPassword`) }] : []),
    ];
    const stepsDisplay = stepsVisible() && currentStep !== null;
    const phoneDestination = getPhoneDestination(recipientPhone.current, t(`${nameSpace}.OTPSteps.yourPhone`));

    return (
        <div className={styles['share-with-external']} data-testid="recipientPage_auth_container">
            {stepsDisplay && (
                <Steps current={currentStep} labelPlacement="vertical" className={styles.stepsContainer} data-testid="recipientPage_auth_steps">
                    {steps.map((step, index) => (
                        <Step key={index} title={step.title} />
                    ))}
                </Steps>
            )}
            {currentStep === null && !(isLoading || anyRequestInProcess) && (<Spinner spinning style={{ margin: 'auto', width: '100%' }} data-testid="recipientPage_auth_spinner" />)}
            {currentStep === stepIndices.emailStep && (
                <OTPAuthEmailBox
                    recipientEmail={newEmail.current || recipientEmailHint}
                    onEmailSubmit={handleEmailSubmit}
                    isLoading={isLoading || anyRequestInProcess}
                    errorMessages={emailAuthFirstStepMessages}
                    textSource={textSource}
                    canSubmitMaskedHint={hasEmailVerification || hasPhoneVerification}
                    data-testid="recipientPage_auth_emailBox"
                />
            )}
            {currentStep === stepIndices.passwordStep && hasPasswordProtection && (
                <>
                    <OTPAuthPasswordBox
                        onPasswordSubmit={handlePasswordSubmit}
                        isLoading={isLoading}
                        passwordError={passwordError}
                        setPasswordError={setPasswordError}
                        wrongPasswordAttempts={wrongPasswordAttempts}
                        hint={passwordHint}
                        data-testid="recipientPage_auth_passwordBox"
                    />
                    {!isUserLoggedIn && (
                        <EmailChangeMessage
                            recipientEmail={newEmail.current || recipientEmail}
                            onEditEmail={() => {
                                setCurrentStep(stepIndices.emailStep);
                                newEmail.current = '';
                                setPasswordError(false);
                            }}
                            data-testid="recipientPage_auth_emailChangeMessage_password"
                        />
                    )}
                </>
            )}
            {currentStep === stepIndices.emailVerificationStep && hasEmailVerification && (
                <OTPAuthCodeBox
                    recipientEmail={newEmail.current || email || recipientEmail}
                    onCodeSubmit={handleCodeSubmit}
                    onResendCode={handleResendCode}
                    onEditEmail={() => {
                        setCurrentStep(stepIndices.emailStep);
                        setTextSource('email');
                        newEmail.current = '';
                        recipientPhone.current = null;
                        setHasPhone(false);
                        resetEmail();
                    }}
                    errorMessages={emailAuthFirstStepMessages}
                    codeError={codeError}
                    setCodeError={setCodeError}
                    data-testid="recipientPage_auth_codeBox_email"
                />
            )}
            {currentStep === stepIndices.phoneVerificationStep && hasPhoneVerification && (
                <>
                    {!isPhoneCodeSent && appConfig.AUTOMATIC_OTP === false ? (
                        <OTPAuthPhoneBox
                            phoneNumber={phoneDestination}
                            onSendCode={async () => {
                                await checkPhoneAndSendCode();
                                setIsPhoneCodeSent(true);
                            }}
                            onSendVoiceCode={async () => {
                                await checkPhoneAndSendVoiceCode();
                                setIsPhoneCodeSent(true);
                            }}
                            isLoading={isLoading || anyRequestInProcess}
                            data-testid="recipientPage_auth_phoneBox"
                        />
                    ) : (
                        <>
                            <OTPAuthCodeBox
                                recipientEmail={phoneDestination}
                                onCodeSubmit={handleCodeSubmit}
                                onResendCode={handleResendCode}
                                onEditEmail={() => {
                                    setCurrentStep(stepIndices.emailStep);
                                    setTextSource('phone');
                                    newEmail.current = '';
                                    recipientPhone.current = null;
                                    setHasPhone(false);
                                    resetEmail();
                                }}
                                codeError={codeError}
                                setCodeError={setCodeError}
                                isPhoneVerification
                                data-testid="recipientPage_auth_codeBox_phone"
                            />
                            {!hasEmailVerification && !isUserLoggedIn && (
                                <EmailChangeMessage
                                    recipientEmail={newEmail.current || recipientEmail}
                                    onEditEmail={() => {
                                        setCurrentStep(stepIndices.emailStep);
                                        setTextSource('phone');
                                        newEmail.current = '';
                                        recipientPhone.current = null;
                                        setHasPhone(false);
                                        resetEmail();
                                    }}
                                    data-testid="recipientPage_auth_emailChangeMessage"
                                />
                            )}
                        </>
                    )}
                </>
            )}
            <PhoneMissingModal
                isOpen={showPhoneMissingModal}
                onCancel={() => {
                    setShowPhoneMissingModal(false);
                }}
                senderEmail={senderEmail}
                data-testid="recipientPage_auth_phoneMissingModal"
            />
            <PasswordMissingModal
                isOpen={showPasswordMissingModal}
                onCancel={() => {
                    setShowPasswordMissingModal(false);
                }}
                senderEmail={senderEmail}
                data-testid="recipientPage_auth_passwordMissingModal"
            />
        </div>
    );
});

export default OTPAuthSteps;
